import React, { useEffect, useMemo, useState } from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import PropTypes from 'prop-types'
import { push } from '@lagunovsky/redux-react-router'
import { flatten, orderBy } from 'lodash'
import i18n from 'simple-react-i18n'
import moment from 'moment'
import { CENTRAL, EQUIPMENT, MAT_CENTRAL, MAT_DIVERS, MAT_EQUIPMENT, MAT_POWER_SUPPLY,
    MAT_SENSOR, MAT_SIM, MAT_SUBSCRIPTION, MAT_TELECOM, POWER_SUPPLY, SENSOR, SIM, SUBSCRIPTION, TELECOM, VARIOUS } from '../../constants/MaterielConstants'
import { ADMINISTRATION, PATH_DASHBOARD, PATH_MATERIEL } from '../../../home/constants/RouteConstants'
import CentralAction from '../central/actions/CentralAction'
import PowerSupplyAction from '../powerSupply/actions/PowerSupplyAction'
import SensorAction from '../sensor/actions/SensorAction'
import TelecomAction from '../telecom/actions/TelecomAction'
import SimAction from '../sim/actions/SimAction'
import VariousMaterielAction from '../variousMateriel/actions/VariousMaterielAction'
import MaterielTypeForm from './MaterielTypeForm'
import ReferencialAction from '../../../referencial/action/ReferencialAction'
import EquipmentAction from '../equipment/actions/EquipmentAction'
import { getMaterielIconValue } from '../../../utils/MaterielUtils'
import ContributorAction from '../../../referencial/components/contributor/actions/ContributorAction'
import MaterielAction from '../../actions/MaterielAction'
import { componentHasHabilitations } from '../../../utils/HabilitationUtil'
import { H_MAT_OPTIONS } from '../../../account/constants/AccessRulesConstants'
import Select from 'components/forms/Select'
import useTitle from 'utils/customHook/useTitle'
import { Card, CardContent, Icon, Dialog, DialogContent, Grid2, Stack } from '@mui/material'
import useActions from 'utils/customHook/useActions'
import { DefaultDialogTitle } from 'components/styled/Dialog'
import CentralTypeDto from '../central/dto/CentralTypeDto'
import DtoSensorType from '../sensor/dto/DtoSensorType'
import EquipmentTypeDto from '../equipment/dto/EquipmentTypeDto'
import DtoSimType from '../sim/dto/DtoSimType'
import TelecomTypeDto from '../telecom/dto/TelecomTypeDto'
import PowerSupplyTypeDto from '../powerSupply/dto/PowerSupplyTypeDto'
import DtoVariousMaterielType from '../variousMateriel/dto/DtoVariousMaterielType'
import SubscriptionTypeDto from '../subscription/dto/SubscriptionTypeDto'
import SubscriptionAction from '../subscription/actions/SubscriptionAction'
import { hasValue } from 'utils/NumberUtil'
import { CardTable } from 'components/datatable/NewTable'
import { darkBlue } from 'utils/constants/ColorTheme'
import { nbPerPageLabel } from 'referencial/constants/ReferencialConstants'
import Input from 'components/forms/Input'
import { normalizeString } from 'utils/StringUtil'
import MaterielTypeDto from 'materiel/dto/MaterielTypeDto'

const DialogNewType = ({
    open,
    materielTypes = [],
    onSelect = () => {},
    onClose = () => {},
}) => (
    <Dialog
        open={open}
        onClose={() => onClose(false)}
    >
        <DefaultDialogTitle title={i18n.materielType} onClose={() => onClose(false)} />
        <DialogContent>
            <Grid2 container spacing={1}>
                {materielTypes.map(t => (
                    <Grid2
                        size={4}
                        onClick={() => onSelect(t)}
                        key={t.materielCategory}
                        container
                        direction='column'
                        alignItems='center'
                        className='clickable'
                    >
                        <Icon fontSize='large'>{getMaterielIconValue(t.materielCategory)}</Icon>
                        {t.materielLabel}
                    </Grid2>
                ))}
            </Grid2>
        </DialogContent>
    </Dialog>
)

DialogNewType.propTypes = {
    open: PropTypes.bool.isRequired,
    materielTypes: PropTypes.arrayOf(PropTypes.instanceOf(MaterielTypeDto)).isRequired,
    onClose: PropTypes.func.isRequired,
    onSelect: PropTypes.func.isRequired,
}

const MaterielTypeApp = () => {
    const [searchValue, setSearchValue] = useState()
    const [selectedMaterielType, setSelectedMaterielType] = useState()
    const [selectedType, setSelectedType] = useState()
    const [isNew, setIsNew] = useState(false)
    const [dialogOpen, setDialogOpen] = useState(false)

    const {
        materielTypes,
        centralTypes,
        powerSupplyTypes,
        sensorTypes,
        telecomTypes,
        simTypes,
        variousMaterielTypes,
        equipmentTypes,
        subscriptionTypes,
    } = useSelector(store => ({
        materielTypes: store.MaterielReducer.materielTypes,
        centralTypes: store.CentralReducer.centralTypes,
        powerSupplyTypes: store.PowerSupplyReducer.powerSupplyTypes,
        sensorTypes: store.SensorReducer.sensorTypes,
        telecomTypes: store.TelecomReducer.telecomTypes,
        simTypes: store.SimReducer.simTypes,
        variousMaterielTypes: store.VariousMaterielReducer.variousMaterielTypes,
        equipmentTypes: store.EquipmentReducer.equipmentTypes,
        subscriptionTypes: store.SubscriptionReducer.subscriptionTypes,
    }), shallowEqual)

    useTitle(() => [{
        title: i18n.materiel,
        href: `${PATH_MATERIEL}/${PATH_DASHBOARD}`,
    }, {
        title: i18n.administration,
        href: `${PATH_MATERIEL}/${ADMINISTRATION}`,
    }, {
        title: i18n.materielTypes,
        href: `${PATH_MATERIEL}/${ADMINISTRATION}/type`,
    }], [])

    const dispatch = useDispatch()

    const onSaveType = () => {
        if (selectedType) {
            const cb = matType => {
                setSelectedType(matType)
                setIsNew(false)
            }
            switch (selectedType.materielCategory) {
                case MAT_CENTRAL:
                    if (isNew) {
                        dispatch(CentralAction.addCentralTypes(selectedType, cb))
                    } else {
                        dispatch(CentralAction.updateCentralTypes(selectedType.materielType, selectedType))
                    }
                    break
                case MAT_POWER_SUPPLY:
                    if (isNew) {
                        dispatch(PowerSupplyAction.addPowerSupplyTypes(selectedType, cb))
                    } else {
                        dispatch(PowerSupplyAction.updatePowerSupplyTypes(selectedType.materielType, selectedType))
                    }
                    break
                case MAT_SENSOR:
                    if (isNew) {
                        dispatch(SensorAction.addSensorTypes(selectedType, cb))
                    } else {
                        dispatch(SensorAction.updateSensorTypes(selectedType.materielType, selectedType))
                    }
                    break
                case MAT_TELECOM:
                    if (isNew) {
                        dispatch(TelecomAction.addTelecomTypes(selectedType, cb))
                    } else {
                        dispatch(TelecomAction.updateTelecomTypes(selectedType.materielType, selectedType))
                    }
                    break
                case MAT_SIM:
                    if (isNew) {
                        dispatch(SimAction.addSimTypes(selectedType, cb))
                    } else {
                        dispatch(SimAction.updateSimTypes(selectedType.materielType, selectedType))
                    }
                    break
                case MAT_DIVERS:
                    if (isNew) {
                        dispatch(VariousMaterielAction.addVariousMaterielTypes(selectedType, cb))
                    } else {
                        dispatch(VariousMaterielAction.updateVariousMaterielTypes(selectedType.materielType, selectedType))
                    }
                    break
                case MAT_EQUIPMENT:
                    const formatType = {
                        ...selectedType,
                        centralType: selectedType.centralType.filter(t => hasValue(t)),
                        sensorType: selectedType.sensorType.filter(t => hasValue(t)),
                        simType: selectedType.simType.filter(t => hasValue(t)),
                        powerSupplyType: selectedType.powerSupplyType.filter(t => hasValue(t)),
                        telecomType: selectedType.telecomType.filter(t => hasValue(t)),
                        variousMaterielType: selectedType.variousMaterielType.filter(t => hasValue(t)),
                    }
                    if (isNew) {
                        dispatch(EquipmentAction.addEquipmentTypes(formatType, cb))
                    } else {
                        dispatch(EquipmentAction.updateEquipmentTypes(formatType.materielType, formatType))
                    }
                    break
                case MAT_SUBSCRIPTION:
                    if (isNew) {
                        dispatch(SubscriptionAction.addSubscriptionTypes(selectedType, cb))
                    } else {
                        dispatch(SubscriptionAction.updateSubscriptionType(selectedType))
                    }
                    break
                default:
                    break
            }
        }
    }

    const onDeleteType = () => {
        switch (selectedType.materielCategory) {
            case MAT_CENTRAL:
                dispatch(CentralAction.deleteCentralType(selectedType.materielType, setSelectedType()))
                break
            case MAT_POWER_SUPPLY:
                dispatch(PowerSupplyAction.deletePowerSupplyType(selectedType.materielType, setSelectedType()))
                break
            case MAT_SENSOR:
                dispatch(SensorAction.deleteSensorType(selectedType.materielType, setSelectedType()))
                break
            case MAT_TELECOM:
                dispatch(TelecomAction.deleteTelecomType(selectedType.materielType, setSelectedType()))
                break
            case MAT_SIM:
                dispatch(SimAction.deleteSimType(selectedType.materielType, setSelectedType()))
                break
            case MAT_DIVERS:
                dispatch(VariousMaterielAction.deleteVariousMaterielType(selectedType.materielType, setSelectedType()))
                break
            case MAT_EQUIPMENT:
                dispatch(EquipmentAction.deleteEquipmentType(selectedType.materielType, setSelectedType()))
                break
            case MAT_SUBSCRIPTION:
                dispatch(SubscriptionAction.deleteSubscriptionType(selectedType.materielType, setSelectedType()))
                break
            default:
                break
        }
    }

    useActions(() => {
        if (selectedType) {
            return {
                save: onSaveType,
                new: () => setDialogOpen(true),
                delete: () => onDeleteType(selectedType),
            }
        }
        return { new: () => setDialogOpen(true) }
    }, [selectedType])

    useEffect(() => {
        if (!componentHasHabilitations(H_MAT_OPTIONS)) { // A modifier quand react-router sera à jour
            dispatch(push('/unauthorized'))
        }
        dispatch(ContributorAction.fetchContributors())
        dispatch(CentralAction.fetchCentralTypes(true))
        dispatch(PowerSupplyAction.fetchPowerSupplyTypes(true))
        dispatch(SensorAction.fetchSensorTypes(true))
        dispatch(TelecomAction.fetchTelecomTypes(true))
        dispatch(SimAction.fetchSimTypes(true))
        dispatch(VariousMaterielAction.fetchVariousMaterielTypes())
        dispatch(EquipmentAction.fetchEquipmentTypes())
        dispatch(SubscriptionAction.fetchSubscriptionTypes())
        dispatch(ReferencialAction.fetchSandreCodes())

        return () => dispatch(MaterielAction.resetAllMateriels())
    }, [])

    const datas = useMemo(() => {
        const filterBySearchValue = (types) => {
            return orderBy(flatten(types)
                .filter(t => normalizeString(`${t.materielLabel}_${t.name}`).includes(normalizeString(searchValue)))
            , ['materielCategory', 'name'], 'asc')
        }
        const getTypes = type => {
            switch (type) {
                case CENTRAL:
                    return centralTypes
                case POWER_SUPPLY:
                    return powerSupplyTypes
                case SENSOR:
                    return sensorTypes
                case TELECOM:
                    return telecomTypes
                case SIM:
                    return simTypes
                case VARIOUS:
                    return variousMaterielTypes
                case EQUIPMENT:
                    return equipmentTypes
                case SUBSCRIPTION:
                    return subscriptionTypes
                default:
                    return []
            }
        }
        if (selectedMaterielType) {
            return filterBySearchValue(getTypes(selectedMaterielType))
        }
        const types = materielTypes.map(({ code }) => {
            return getTypes(code)
        })
        return filterBySearchValue(types)
    }, [selectedMaterielType, searchValue, materielTypes, centralTypes, powerSupplyTypes, sensorTypes,
        telecomTypes, simTypes, variousMaterielTypes, equipmentTypes, subscriptionTypes])

    const types = datas.map(type => {
        const {
            materielCategory, endDate, materielLabel, label, comment,
        } = type
        return {
            nullValue: <Icon className='clickable' onClick={() => setSelectedType(type)}>edit</Icon>,
            icon: <Icon>{getMaterielIconValue(materielCategory)}</Icon>,
            materielLabel,
            label,
            comment,
            color: endDate && endDate < moment().valueOf() ? '#cccccc' : 'white',
        }
    })

    const getNewType = type => {
        switch (type) {
            case CENTRAL: return new CentralTypeDto({})
            case SENSOR: return new DtoSensorType({})
            case EQUIPMENT: return new EquipmentTypeDto({})
            case SIM: return new DtoSimType({})
            case TELECOM: return new TelecomTypeDto({})
            case POWER_SUPPLY: return new PowerSupplyTypeDto({})
            case VARIOUS: return new DtoVariousMaterielType({})
            case SUBSCRIPTION: return new SubscriptionTypeDto({})
            default: return {}
        }
    }

    return (
        <Stack spacing={1}>
            <Card>
                <CardContent>
                    <Grid2 container spacing={1}>
                        <Grid2 size={3}>
                            <Input
                                containerClassName='no-margin'
                                title={ i18n.search }
                                value={ searchValue }
                                onChange={(v) => setSearchValue(v)}
                            />
                        </Grid2>
                        <Grid2 size={3}>
                            <Select
                                options={materielTypes}
                                label={i18n.type}
                                nullLabel='&nbsp;'
                                onChange={value => setSelectedMaterielType(value != -1 ? value : undefined)}
                                value={selectedMaterielType}
                            />
                        </Grid2>
                    </Grid2>
                </CardContent>
            </Card>
            <Grid2 container spacing={1}>
                <Grid2 size={6}>
                    <CardTable
                        title={i18n.materielTypes}
                        rows={types}
                        headers={['nullValue', 'icon', { key: 'materielLabel', value: i18n.type }, 'label', 'comment']}
                        color={darkBlue}
                        rowsPerPageOptions={nbPerPageLabel}
                    />
                </Grid2>
                <Grid2 size={6}>
                    <MaterielTypeForm materielType={selectedType} isNew={isNew} onChange={setSelectedType} />
                </Grid2>
            </Grid2>
            <DialogNewType
                open={dialogOpen}
                materielTypes={materielTypes.filter(({ code }) => code).map(({ code }) => getNewType(code))}
                onClose={() => setDialogOpen(false)}
                onSelect={(t) => {
                    setSelectedType(t)
                    setIsNew(true)
                    setDialogOpen(false)
                }}
            />
        </Stack>
    )
}

export default MaterielTypeApp
